// La lógica de la agenda, pura: prioridad, filtros, orden, conteos y métricas. Recibe las filas y
// el día de hoy, y no toca el DOM ni el reloj: la pantalla pinta, esto decide.
import type {
    AgendaContact,
    AgendaFilters,
    AgendaMetrics,
    AgendaPriority,
    AgendaSort,
    AgendaStatKey,
    AgendaStats,
    AgendaPromise,
    CollectorAgendaRow,
} from './types';

const MS_PER_DAY = 86_400_000;

/** Solo la parte `YYYY-MM-DD` de una marca de tiempo: comparar con la hora da días negativos. */
export function toDay(at: string): string {
    return at.slice(0, 10);
}

/** Días de calendario entre dos días. Nunca negativo: un contacto del futuro cuenta como hoy. */
export function daysBetween(from: string, to: string): number {
    const start = new Date(`${toDay(from)}T00:00:00`).getTime();
    const end = new Date(`${toDay(to)}T00:00:00`).getTime();
    return Math.max(0, Math.floor((end - start) / MS_PER_DAY));
}

/** El contacto más reciente. `null` cuando nunca se lo contactó. */
export function lastContactOf(row: CollectorAgendaRow): AgendaContact | null {
    let latest: AgendaContact | null = null;
    for (const contact of row.contacts) {
        if (latest === null || contact.at > latest.at) latest = contact;
    }
    return latest;
}

export function activePromiseOf(row: CollectorAgendaRow): AgendaPromise | null {
    return row.promises.find((promise) => promise.state === 'active') ?? null;
}

export function brokenPromiseOf(row: CollectorAgendaRow): AgendaPromise | null {
    return row.promises.find((promise) => promise.state === 'broken') ?? null;
}

export function isLocked(row: CollectorAgendaRow): boolean {
    return row.device?.state === 'locked';
}

/**
 * La prioridad con la que la agenda se recorre (M4 §4.3). Los incumplidos van primero por regla de
 * negocio: sin contacto en 24 h el supervisor recibe alerta (M4 §4.5).
 */
export function computePriority(row: CollectorAgendaRow, today: string): AgendaPriority | null {
    if (brokenPromiseOf(row) !== null) return { rank: 1, label: 'Incumplido' };
    const active = activePromiseOf(row);
    if (active !== null && active.day === today) return { rank: 2, label: 'Compromiso hoy' };
    if (row.overdueDays > 30) return { rank: 3, label: 'Mora >30d' };
    if (row.overdueDays >= 8) return { rank: 4, label: 'Mora 8-30d' };
    if (row.overdueDays >= 1) return { rank: 5, label: 'Mora 1-7d' };
    return null;
}

/** Días desde el último contacto. `null` cuando no hay ninguno, que NO es cero. */
export function daysSinceLastContact(row: CollectorAgendaRow, today: string): number | null {
    const last = lastContactOf(row);
    return last === null ? null : daysBetween(last.at, today);
}

function matchesArrearsBucket(overdueDays: number, bucket: AgendaFilters['arrears']): boolean {
    switch (bucket) {
        case '':
            return true;
        case '1-7':
            return overdueDays >= 1 && overdueDays <= 7;
        case '8-30':
            return overdueDays >= 8 && overdueDays <= 30;
        case '31-60':
            return overdueDays >= 31 && overdueDays <= 60;
        case '61-90':
            return overdueDays >= 61 && overdueDays <= 90;
        case '90+':
            return overdueDays >= 90;
    }
}

function matchesChannel(
    row: CollectorAgendaRow,
    channel: AgendaFilters['channel'],
    today: string,
): boolean {
    if (channel === '') return true;
    const last = lastContactOf(row);
    // Sin contacto registrado, «sin contacto en N» siempre aplica y ningún canal concreto lo hace.
    if (channel === 'no_contact_24h') return last === null || daysBetween(last.at, today) >= 1;
    if (channel === 'no_contact_3d') return last === null || daysBetween(last.at, today) >= 3;
    return last !== null && last.channel === channel;
}

function matchesSearch(row: CollectorAgendaRow, search: string): boolean {
    const needle = search.trim().toLowerCase();
    if (needle === '') return true;
    return (
        row.creditId.toLowerCase().includes(needle) ||
        row.customerName.toLowerCase().includes(needle) ||
        (row.device?.imei.includes(needle) ?? false)
    );
}

export function filterAgenda(
    rows: readonly CollectorAgendaRow[],
    filters: AgendaFilters,
    today: string,
): CollectorAgendaRow[] {
    return rows.filter((row) => {
        if (!matchesSearch(row, filters.search)) return false;
        if (filters.store !== '' && row.storeId !== filters.store) return false;
        if (!matchesArrearsBucket(row.overdueDays, filters.arrears)) return false;
        if (!matchesChannel(row, filters.channel, today)) return false;
        if (filters.minAmountCents !== null && row.overdueAmountCents < filters.minAmountCents) {
            return false;
        }
        if (filters.maxAmountCents !== null && row.overdueAmountCents > filters.maxAmountCents) {
            return false;
        }
        if (filters.flags.includes('with_promise') && activePromiseOf(row) === null) return false;
        if (filters.flags.includes('broken_promise') && brokenPromiseOf(row) === null) return false;
        if (filters.flags.includes('locked_device') && !isLocked(row)) return false;
        return true;
    });
}

/** El recorte de la pastilla elegida arriba de la tabla; se compone con el panel de filtros. */
export function applyStatFilter(
    rows: readonly CollectorAgendaRow[],
    stat: AgendaStatKey,
    today: string,
): CollectorAgendaRow[] {
    switch (stat) {
        case 'all':
            return [...rows];
        case 'broken':
            return rows.filter((row) => brokenPromiseOf(row) !== null);
        case 'today':
            return rows.filter((row) => activePromiseOf(row)?.day === today);
        case 'over30':
            return rows.filter((row) => row.overdueDays > 30);
        case 'locked':
            return rows.filter((row) => isLocked(row));
    }
}

/** Cuántos filtros están puestos, para el contador del encabezado de la columna. */
export function activeFilterCount(filters: AgendaFilters): number {
    let count = 0;
    if (filters.search.trim() !== '') count += 1;
    if (filters.store !== '') count += 1;
    if (filters.arrears !== '') count += 1;
    if (filters.channel !== '') count += 1;
    if (filters.minAmountCents !== null) count += 1;
    if (filters.maxAmountCents !== null) count += 1;
    return count + filters.flags.length;
}

function sortValue(
    row: CollectorAgendaRow,
    key: AgendaSort['key'],
    today: string,
): string | number {
    switch (key) {
        case 'priority':
            return computePriority(row, today)?.rank ?? 99;
        case 'creditId':
            return row.creditId;
        case 'customer':
            return row.customerName;
        case 'overdueInstallments':
            return row.overdueInstallments;
        case 'overdueAmount':
            return row.overdueAmountCents;
        case 'overdueDays':
            return row.overdueDays;
        case 'lastContact':
            return lastContactOf(row)?.at ?? '0000';
        case 'promise':
            return activePromiseOf(row)?.day ?? '9999';
    }
}

export function sortAgenda(
    rows: readonly CollectorAgendaRow[],
    sort: AgendaSort,
    today: string,
): CollectorAgendaRow[] {
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((left, right) => {
        const a = sortValue(left, sort.key, today);
        const b = sortValue(right, sort.key, today);
        if (a < b) return -direction;
        if (a > b) return direction;
        return 0;
    });
}

export function computeAgendaStats(
    rows: readonly CollectorAgendaRow[],
    today: string,
): AgendaStats {
    return {
        all: rows.length,
        broken: rows.filter((row) => brokenPromiseOf(row) !== null).length,
        today: rows.filter((row) => activePromiseOf(row)?.day === today).length,
        over30: rows.filter((row) => row.overdueDays > 30).length,
        locked: rows.filter((row) => isLocked(row)).length,
    };
}

/**
 * Las tres métricas de la barra de herramientas. «Recuperado hoy» suma una cuota por cada promesa
 * de pago tomada hoy por teléfono, que es lo que el mock cuenta: no hay pagos con fecha en el silo.
 */
export function computeAgendaMetrics(
    rows: readonly CollectorAgendaRow[],
    today: string,
): AgendaMetrics {
    const recoveredTodayCents = rows.reduce((total, row) => {
        const promisesToday = row.contacts.filter(
            (contact) =>
                contact.channel === 'call' &&
                contact.outcome === 'promise' &&
                toDay(contact.at) === today,
        ).length;
        return total + promisesToday * row.weeklyPaymentWithIvaCents;
    }, 0);

    let closed = 0;
    let fulfilled = 0;
    for (const row of rows) {
        for (const promise of row.promises) {
            if (promise.state !== 'active') closed += 1;
            if (promise.state === 'fulfilled') fulfilled += 1;
        }
    }

    const noContactThreeDays = rows.filter((row) => {
        const days = daysSinceLastContact(row, today);
        return days === null || days >= 3;
    }).length;

    return {
        recoveredTodayCents,
        cureRatePercent: closed === 0 ? 0 : Math.round((fulfilled / closed) * 100),
        noContactThreeDays,
    };
}

/** El tramo de mora que se lee bajo el DPI en la ficha. */
export function arrearsBucketLabel(overdueDays: number): string {
    if (overdueDays > 90) return 'Cartera vencida';
    if (overdueDays > 30) return 'Reporte a Buró';
    if (overdueDays > 7) return 'Mora media';
    if (overdueDays > 0) return 'Mora leve';
    return 'Al día';
}

export interface AgendaBanner {
    text: string;
    tone: 'info' | 'warning' | 'danger' | 'success';
}

/** La regla que el cliente tiene que cumplir para que se le desbloquee el equipo (M2 §7.3, M4 §2.3). */
export function unlockRule(
    row: CollectorAgendaRow,
    formatMoney: (cents: number) => string,
): AgendaBanner {
    if (row.accumulatedArrears >= 3) {
        return {
            tone: 'danger',
            text: `Reincidencia (${String(row.accumulatedArrears)} atrasos vitalicios): saldar TODA la deuda vencida (${formatMoney(row.overdueAmountCents)}) para desbloquear.`,
        };
    }
    if (row.overdueInstallments >= 3) {
        return {
            tone: 'warning',
            text: `${String(row.overdueInstallments)} cuotas vencidas: pagar al menos 50% del saldo vencido (${formatMoney(Math.round(row.overdueAmountCents * 0.5))}) para desbloquear.`,
        };
    }
    if (row.overdueInstallments >= 1) {
        return {
            tone: 'info',
            text: `Pagar 1 cuota completa (${formatMoney(row.weeklyPaymentWithIvaCents)}) para desbloquear el dispositivo.`,
        };
    }
    return { tone: 'success', text: 'Sin cuotas vencidas actualmente.' };
}

/** El aviso contextual del pie de la pestaña Agenda: qué tiene que hacer el cobrador ahora (M4 §7). */
export function contextAlert(
    row: CollectorAgendaRow,
    today: string,
    formatMoney: (cents: number) => string,
    formatDay: (day: string) => string,
    promiseChannelLabel: (channel: AgendaPromise['channel']) => string,
): AgendaBanner {
    const broken = brokenPromiseOf(row);
    if (broken !== null) {
        return {
            tone: 'danger',
            text: `Compromiso incumplido del ${formatDay(broken.day)}. Registrar nuevo contacto en 24h o alerta al supervisor.`,
        };
    }
    const active = activePromiseOf(row);
    if (active !== null && active.day === today) {
        return {
            tone: 'info',
            text: `Compromiso HOY: ${formatMoney(active.amountCents)} vía ${promiseChannelLabel(active.channel)}. Registrar pago al recibir confirmación.`,
        };
    }
    if (row.overdueDays >= 30) {
        return {
            tone: 'danger',
            text: `${String(row.overdueDays)} días de mora — reportado a Círculo de Crédito (vía GSF). Gestión intensiva. Evaluar reestructura si no lo intentó.`,
        };
    }
    if (row.overdueDays >= 14) {
        return {
            tone: 'warning',
            text: `Día ${String(row.overdueDays)} de mora — ofrecer reestructura al cliente (M4 §6.3). Máx 1 por crédito.`,
        };
    }
    return {
        tone: 'info',
        text: 'Mora temprana. Contact center + blaster (M4 §4.5). Mínimo 3 intentos antes de escalar.',
    };
}
